import { useEffect } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {
  EditorForm,
  Field,
  inputClasses,
  textareaClasses,
  type EditorSectionProps,
} from './shared';
import { heroSchema, type HeroValues } from '../schemas';
import { MediaInput } from '../components/MediaInput';

const AVAILABILITY: ReadonlyArray<{ id: HeroValues['availability']; label: string; hint: string }> = [
  { id: 'available', label: 'Available', hint: 'Open to new work' },
  { id: 'limited', label: 'Limited', hint: 'Selective inquiries' },
  { id: 'closed', label: 'Closed', hint: 'Not taking work' },
];

export const HERO_SEED: HeroValues = {
  portraitUrl: '',
  tagline: '',
  headline: '',
  subheadline: '',
  ctaLabel: '',
  ctaHref: '',
  availability: 'available',
};

export function HeroEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<HeroValues>) {
  const form = useForm<HeroValues>({
    resolver: zodResolver(heroSchema) as Resolver<HeroValues>,
    defaultValues: { ...HERO_SEED, ...initial },
  });

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm
      onSubmit={form.handleSubmit(async (v) => onSubmit(v))}
      submitting={submitting}
      disabled={form.formState.isSubmitting}
    >
      <Field label="Portrait" hint="1:1 square. PNG, JPG, or WebP. Max 6 MB.">
        <Controller
          control={form.control}
          name="portraitUrl"
          render={({ field }) => (
            <MediaInput
              kind="image"
              value={field.value ?? ''}
              onChange={field.onChange}
              purpose="hero-portrait"
            />
          )}
        />
      </Field>

      <Field
        label="Tagline"
        hint="A short over-line. Shown above your headline."
        error={errors.tagline?.message}
      >
        <input
          {...form.register('tagline')}
          placeholder="Portfolio · 2026"
          className={inputClasses}
        />
      </Field>

      <Field
        label="Headline"
        hint="A single declarative sentence."
        error={errors.headline?.message}
      >
        <input
          {...form.register('headline')}
          placeholder="A structural field guide to my work."
          className={inputClasses}
        />
      </Field>

      <Field
        label="Subheadline"
        hint="One or two sentences of context."
        error={errors.subheadline?.message}
      >
        <textarea
          {...form.register('subheadline')}
          rows={3}
          placeholder="Independent designer — working across product, brand, and editorial."
          className={textareaClasses}
        />
      </Field>

      <div className="grid grid-cols-[1fr_1.5fr] gap-4">
        <Field label="CTA label" error={errors.ctaLabel?.message}>
          <input
            {...form.register('ctaLabel')}
            placeholder="See projects"
            className={inputClasses}
          />
        </Field>
        <Field label="CTA link" hint="Optional. Leave empty to hide." error={errors.ctaHref?.message}>
          <input {...form.register('ctaHref')} placeholder="https://" className={inputClasses} />
        </Field>
      </div>

      <Field label="Availability" hint="Shown as a status badge on your hero.">
        <Controller
          control={form.control}
          name="availability"
          render={({ field }) => (
            <RadioGroup.Root
              value={field.value}
              onValueChange={field.onChange}
              className="grid grid-cols-3 gap-2"
            >
              {AVAILABILITY.map((opt) => (
                <RadioGroup.Item
                  key={opt.id}
                  value={opt.id}
                  className="group rounded-lg border border-line bg-board p-3 text-left transition-colors data-[state=checked]:border-ink data-[state=checked]:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/20"
                >
                  <span className="block text-sm font-medium text-ink">{opt.label}</span>
                  <span className="mt-0.5 block text-[11px] text-ink-faint">{opt.hint}</span>
                </RadioGroup.Item>
              ))}
            </RadioGroup.Root>
          )}
        />
      </Field>
    </EditorForm>
  );
}
